import type { EventSettings } from '../api/generated/EventSettings'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import type { ShiftFields } from '../api/generated/ShiftFields'
import { addMinutes } from '../shifts/time'

/** A shift on one day; `start` and `end` are minutes since midnight, clipped to the day. */
export type GridBlock = { shift: Shift; start: number; end: number; lane: number }

/** A position's shifts on one day; overlapping shifts go to separate lanes. */
export type GridRow = { position: Position; lanes: number; blocks: GridBlock[] }

/** One day of the planning, midnight to midnight, a row per position; opens on `scrollHour`. */
export type DayGrid = { scrollHour: number; rows: GridRow[] }

const DAY_MS = 86_400_000
const DAY_MINUTES = 1440
/** Hour a day without shifts opens on. */
const DEFAULT_SCROLL_HOUR = 8

// Local times without offset, handled as UTC like `shifts/time.ts`.
const asDate = (local: string) => new Date(`${local}Z`)
const dayOf = (date: Date) => date.toISOString().slice(0, 10)

/** `YYYY-MM-DDTHH:MM:SS` of `instant` in `timezone`, the format of shift times. */
export function localDateTime(instant: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const part = (type: string) => parts.find((p) => p.type === type)?.value
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`
}

/** `YYYY-MM-DD` of `instant` in `timezone`. */
export function localDate(instant: Date, timezone: string): string {
  return localDateTime(instant, timezone).slice(0, 10)
}

/**
 * Days of the event (its dates, when set) and days covered by the shifts, in order; a shift
 * ending at midnight does not cover the next day.
 */
export function planningDays(
  shifts: Shift[],
  dates?: Pick<EventSettings, 'starts_on' | 'ends_on'>,
): string[] {
  const days = new Set<string>()
  if (dates?.starts_on && dates.ends_on) {
    const last = asDate(`${dates.ends_on}T00:00:00`).getTime()
    for (let day = asDate(`${dates.starts_on}T00:00:00`).getTime(); day <= last; day += DAY_MS)
      days.add(dayOf(new Date(day)))
  }
  for (const shift of shifts) {
    const end = asDate(shift.ends_at).getTime()
    let day = asDate(`${shift.starts_at.slice(0, 10)}T00:00:00`).getTime()
    for (; day < end; day += DAY_MS) days.add(dayOf(new Date(day)))
  }
  return [...days].sort()
}

/** Today when the planning covers it, otherwise its first day. */
export function defaultDay(days: string[], today: string): string | undefined {
  return days.includes(today) ? today : days[0]
}

function withLanes(blocks: Omit<GridBlock, 'lane'>[]): { lanes: number; blocks: GridBlock[] } {
  const laneEnds: number[] = []
  const placed = [...blocks]
    .sort((a, b) => a.start - b.start || a.end - b.end)
    .map((block) => {
      let lane = laneEnds.findIndex((end) => end <= block.start)
      if (lane === -1) lane = laneEnds.length
      laneEnds[lane] = block.end
      return { ...block, lane }
    })
  return { lanes: laneEnds.length, blocks: placed }
}

/**
 * The grid of `day`, a row per position in the order of `positions`, opening on its first shift
 * (8 h without any); skips shifts of positions not loaded.
 */
export function dayGrid(day: string, shifts: Shift[], positions: Position[]): DayGrid {
  const dayStart = asDate(`${day}T00:00:00`).getTime()
  const minutes = (local: string) => Math.round((asDate(local).getTime() - dayStart) / 60_000)
  const byPosition = new Map<string, Omit<GridBlock, 'lane'>[]>()
  for (const shift of shifts) {
    const start = Math.max(minutes(shift.starts_at), 0)
    const end = Math.min(minutes(shift.ends_at), DAY_MINUTES)
    if (start >= end) continue
    const blocks = byPosition.get(shift.position_id) ?? []
    blocks.push({ shift, start, end })
    byPosition.set(shift.position_id, blocks)
  }

  const rows = positions.map((position) => {
    const { lanes, blocks } = withLanes(byPosition.get(position.id) ?? [])
    return { position, lanes: Math.max(lanes, 1), blocks }
  })
  const all = rows.flatMap((row) => row.blocks)
  return {
    scrollHour: all.length
      ? Math.floor(Math.min(...all.map((b) => b.start)) / 60)
      : DEFAULT_SCROLL_HOUR,
    rows,
  }
}

/** The one-hour shift a click on `hour` of `day` prefills. */
export function shiftAt(day: string, hour: number): ShiftFields {
  const startsAt = `${day}T${String(hour).padStart(2, '0')}:00:00`
  return { starts_at: startsAt, ends_at: addMinutes(startsAt, 60), required_volunteers: 1 }
}
